'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1B4D4A] to-[#2A6B67] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{color: '#99C5C2'}}>Get in Touch</p>
          <h1 className="font-serif text-5xl text-white mb-4" style={{fontFamily: 'Georgia, serif'}}>Contact</h1>
          <p className="text-white/80 text-base">
            <Link href="/" className="hover:text-white">Home</Link> <span className="mx-2">/</span> Contact
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-white pt-20 pb-[75px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            {/* Form */}
            <div>
              <p className="text-[#1B4D4A] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Send a Message</p>
              <h2 className="font-serif text-4xl text-gray-800 mb-6" style={{fontFamily: 'Georgia, serif'}}>Get in Touch</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Whether you have a question, want to find out more about counselling, or are ready to book your first session, please get in touch. I aim to respond within 24 hours.
              </p>

              {submitted ? (
                <div className="bg-[#E8F0EF] rounded-xl p-8 text-center">
                  <svg className="w-12 h-12 text-[#1B4D4A] mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <h3 className="font-semibold text-[#1B4D4A] text-lg mb-2">Message Sent!</h3>
                  <p className="text-gray-600 text-sm">Thank you for getting in touch. I will respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number (optional)</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent"
                      placeholder="07700 000000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Message *</label>
                    <textarea
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent resize-none"
                      placeholder="Tell me a little about what has brought you here..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#1B4D4A] text-white py-3.5 rounded-lg text-base font-medium hover:bg-[#2A6B67] transition-colors"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Contact Details */}
            <div>
              <p className="text-[#1B4D4A] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Contact Details</p>
              <h2 className="font-serif text-4xl text-gray-800 mb-8" style={{fontFamily: 'Georgia, serif'}}>How to Reach Me</h2>

              <div className="space-y-6 mb-10">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                    ),
                    label: 'Phone',
                    value: '+44 7462 291154',
                    href: 'tel:+447462291154',
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                    ),
                    label: 'Email',
                    value: 'contact@harbourcounsellingpractice.com',
                    href: 'mailto:contact@harbourcounsellingpractice.com',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#E8F0EF] rounded-lg flex items-center justify-center text-[#1B4D4A] flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">{item.label}</p>
                      <a href={item.href} className="text-gray-800 text-sm hover:text-[#1B4D4A] transition-colors">{item.value}</a>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#F5F5F3] rounded-xl p-6">
                <h3 className="font-semibold text-gray-800 text-base mb-3">Response Time</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  I aim to respond to all enquiries within 24 hours during working days. If you are in crisis, please contact the Samaritans on <strong>116 123</strong> or your GP.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
